import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PipeRule } from '../components/prototype-ui'
import { PricingTierCard, type PricingTier } from '../components/ui/PricingTierCard'

const TIERS: PricingTier[] = [
  { name: 'Free', price: '$0', cadence: '/forever',
    desc: 'Try Instadash. Private by default, no credit card.',
    features: ['3 grids · public or private', '1k rows / grid', '7-day version history', 'CLI upload', 'Community Discord'],
    cta: 'Start free' },
  { name: 'Builder', price: '$19', cadence: '/mo', highlight: true,
    desc: 'For indie hackers, vibe coders, and small teams.',
    features: ['Unlimited grids', '100k rows / grid', '30-day version history', 'CSV / JSON / NDJSON', 'API endpoints (/rows, /meta, /versions)', 'LLM context files', 'Embeddable grids'],
    cta: 'Upgrade to Builder' },
  { name: 'Agent', price: '$79', cadence: '/mo',
    desc: 'For agents that never sleep.',
    features: ['5M rows / grid', 'Hosted MCP server', 'Grid search API', 'Webhooks · scheduled refresh', 'Team sharing', 'Higher API limits'],
    cta: 'Upgrade to Agent' },
  { name: 'Platform', price: 'Custom', cadence: '',
    desc: "For teams that won't put data in our infra.",
    features: ['BYO Cloudflare account', 'Custom domain', 'SSO + audit logs', 'Private mesh sub-network', 'On-prem / self-hosted Worker', 'SLA'],
    cta: 'Contact sales' },
]

const FAQ = [
  { q: 'When am I billed?',
    a: 'Free is free forever. Builder and Agent bill monthly via Stripe after you sign in.' },
  { q: 'What counts as a row?',
    a: 'Each line in your JSONL upload is one row. Re-pushing the same grid replaces all rows; old versions stay in version history.' },
  { q: 'What about R2 storage costs?',
    a: "You're using Instadash's R2 bucket — storage is included in the plan. Platform tier brings your own R2 bucket for sovereignty." },
  { q: 'Can I move my data out?',
    a: 'Always. Every grid is JSONL in R2. The CLI exports any version. We follow open formats by default.' },
]

export default function Pricing() {
  const navigate = useNavigate()
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)

  async function handleCheckout(plan: 'builder' | 'agent') {
    setCheckoutLoading(plan)
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      })
      if (res.status === 401) { navigate('/workspace'); return }
      if (!res.ok) throw new Error('Checkout failed')
      const { url } = await res.json() as { url: string }
      window.location.href = url
    } finally {
      setCheckoutLoading(null)
    }
  }

  return (
    <>
      {/* Hero */}
      <section style={{ padding: '64px 0 24px' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="eyebrow"><span className="num">$</span>pricing</div>
          <h1 className="h-display" style={{ fontSize: 'clamp(40px, 5vw, 72px)', marginTop: 14 }}>
            Start free.<br/>
            <span className="accent">Pay when grids go big.</span>
          </h1>
          <p className="lede" style={{ margin: '20px auto 0', textAlign: 'center', fontSize: 17, maxWidth: 600 }}>
            Private grids included on every plan. Pay for rows, retention, and agent access.
            No per-seat tax.
          </p>
        </div>
      </section>

      {/* Tier cards */}
      <section className="section" style={{ paddingTop: 32 }}>
        <div className="container">
          <div className="resp-grid-4" style={{ gap: 14 }}>
            {TIERS.map(t => {
              const isLoading = checkoutLoading === t.name.toLowerCase()
              return (
                <PricingTierCard
                  key={t.name}
                  tier={t}
                  disabled={checkoutLoading !== null}
                  ctaOverride={isLoading ? 'Redirecting…' : `${t.cta} →`}
                  onSelect={() => {
                    if (t.name === 'Builder') handleCheckout('builder')
                    else if (t.name === 'Agent') handleCheckout('agent')
                    else if (t.name === 'Platform') window.location.href = 'mailto:hello@instadash.io'
                    else navigate('/get-started')
                  }}
                />
              )
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <div className="eyebrow"><span className="num">?</span>faq</div>
          <h2 className="h1" style={{ marginTop: 14, marginBottom: 36 }}>Common questions.</h2>
          <div className="resp-grid-2" style={{ gap: 20 }}>
            {FAQ.map(f => (
              <div key={f.q} className="card card-pad">
                <div className="h3" style={{ marginBottom: 10 }}>{f.q}</div>
                <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '64px 0 80px' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <PipeRule label="ready" />
          <h2 className="h-display" style={{ fontSize: 'clamp(36px, 4.5vw, 56px)', marginTop: 24, marginBottom: 16 }}>
            Free forever. Private by default.
          </h2>
          <p className="lede" style={{ margin: '0 auto', textAlign: 'center', fontSize: 16 }}>
            No card. No DB. No frontend.
          </p>
          <div className="row gap-12" style={{ justifyContent: 'center', marginTop: 28 }}>
            <button className="btn btn-primary btn-lg btn-mono" onClick={() => navigate('/get-started')}>npm i -g instadash</button>
            <button className="btn btn-ghost btn-lg" onClick={() => navigate('/docs')}>Browse docs →</button>
          </div>
        </div>
      </section>
    </>
  )
}
